import { canSendToGTM } from "./analiticsConfigByFacility";
import { ConfirmationData } from "../../app/models/ConfirmationData";
import { PurchaseItem } from "../../app/models/PurchaseItem";
import { PurchaseEvent } from "../../app/models/PurchaseEvent";
import { BeginCheckoutEvent } from "../../app/models/BeginCheckoutEvent";
import { BeginCheckoutItem } from "../../app/models/BeginCheckoutItem";
import { AddToCartEvent } from "../../app/models/AddToCartEvent";
import { RemoveFromCartEvent } from "../../app/models/RemoveFromCartEvent";
import { ViewCartEvent } from "../../app/models/ViewCartEvent";
import { format } from "date-fns";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Backend zwraca liczby raz z przecinkiem jako separatorem dziesiętnym (np. "36,00" w
 * ConfirmationData.total), a raz z kropką (np. "36.0" w cenach linii) — parseFloat/parseInt
 * po prostu ucina wszystko od przecinka ("45,99" -> 45), więc trzeba znormalizować przed parsowaniem.
 */
const parseLocaleNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;

  const trimmed = value.trim();
  if (trimmed === "") return 0;

  const parsed = parseFloat(trimmed.replace(",", "."));
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Zawsze do strony nadrzędnej — odbiór i mapowanie na GTM/GA/Pixel jest po stronie klienta. */
const forwardEcommerceToParent = (eventName: string, ecommerce: unknown): void => {
  try {
    const message = JSON.stringify({
      name: "gtmAnalytics",
      gtmData: {
        event: eventName,
        ecommerce,
      },
    });

    console.log(`🔥 Sending postMessage ${eventName} to parent (GTM):`, message);
    window.parent.postMessage(message, "*");
  } catch (error) {
    console.error(`❌ postMessage error (${eventName}):`, error);
  }
};

const pushLocalGtmEvent = (facilityId: string, eventName: string, ecommerce: unknown): void => {
  if (!canSendToGTM(facilityId)) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ecommerce,
  });
  console.info(`📦 GTM (local) event sent: ${eventName}`, ecommerce);
};

export function sendAddToCartEvent(facilityId: string, data: AddToCartEvent): void {
  pushLocalGtmEvent(facilityId, "add_to_cart", data);
  forwardEcommerceToParent("add_to_cart", data);
}

/**
 * Wysyła event remove_from_cart do GTM i do rodzica (postMessage jako JSON)
 * @param facilityId - ID obiektu
 * @param data - Dane produktu usuniętego z koszyka
 */
export function sendRemoveFromCartEvent(facilityId: string, data: RemoveFromCartEvent): void {
  pushLocalGtmEvent(facilityId, "remove_from_cart", data);
  forwardEcommerceToParent("remove_from_cart", data);
}

/**
 * Wysyła event view_cart do GTM
 * @param facilityId - ID obiektu
 * @param data - Dane koszyka do wyświetlenia
 */
export const sendViewCartEvent = (facilityId: string, data: ViewCartEvent): void => {
  pushLocalGtmEvent(facilityId, "view_cart", data);
  forwardEcommerceToParent("view_cart", data);
};

/**
 * Wysyła event begin_checkout do GTM
 * @param facilityId - ID obiektu
 * @param data - Dane rozpoczęcia procesu checkout
 */
export const sendBeginCheckoutEvent = (facilityId: string, data: BeginCheckoutEvent): void => {
  pushLocalGtmEvent(facilityId, "begin_checkout", data);
  forwardEcommerceToParent("begin_checkout", data);
};

/**
 * Wysyła event purchase do GTM
 * @param facilityId - ID obiektu
 * @param data - Dane zakończonego zakupu
 */
export const sendPurchaseEvent = (facilityId: string, data: PurchaseEvent): void => {
  pushLocalGtmEvent(facilityId, "purchase", data);
  forwardEcommerceToParent("purchase", data);
};


export const convertBasketToBeginCheckoutFormat = (
  basket: any[],
  total: number,
  promoCode?: string
): BeginCheckoutEvent => {
  const items: BeginCheckoutItem[] = basket.map((item) => {
    const itemCategory = (item.ticketAccess && item.ticketAccess[0]?.exerciseName) || getPolishItemCategory(item);
    let selectedDay: string | undefined;

    if (item.ticketAccess && item.ticketAccess[0]?.startDate) {
      try {
        const date = new Date(item.ticketAccess[0].startDate);
        selectedDay = format(date, "yyyy-MM-dd");
      } catch (error) {
        console.warn("Error formatting ticketAccess startDate:", error);
      }
    } else if (item.validFrom) {
      try {
        const date = new Date(item.validFrom);
        selectedDay = format(date, "yyyy-MM-dd");
      } catch (error) {
        console.warn("Error formatting validFrom date:", error);
      }
    }

    const itemName = item.assortName || undefined;

    return {
      item_id: item.assortIds || "",
      item_name: itemName,
      item_category: itemCategory,
      price: item.price || 0,
      quantity: item.qnt || 1,
      selected_day: selectedDay,
    };
  });

  return {
    currency: "PLN",
    value: total,
    coupon: promoCode || undefined,
    items,
  };
};

/**
 * Pomocnicza funkcja do konwersji danych potwierdzenia na format dla GTM
 * @param confirmationData - Dane potwierdzenia zakupu
 * @returns Dane w formacie GTM dla eventu purchase
 */
export const convertConfirmationDataToGtmFormat = (confirmationData: ConfirmationData): PurchaseEvent => {
  // Zabezpieczenie przed brakiem danych (wsteczna kompatybilność)
  const lines = confirmationData?.lines || [];
  const transactionId = confirmationData?.transactionId || "";
  const total = parseLocaleNumber(confirmationData?.total);
  const discountCode = confirmationData?.discountCode || undefined;
  const discountValue = confirmationData?.discountValue || undefined;
  const payName = confirmationData?.payName || undefined;

  const items: PurchaseItem[] = lines.map((line: any) => {
    // Używamy polskich nazw kategorii również w evencie purchase
    const itemCategory =
      line?.exerciseName || line?.exerciseGroupName || getPolishItemCategory(line) || line?.assortName || undefined;

    return {
      item_id: line?.assortIds || "",
      item_name: line?.assortName || "Brak nazwy",
      item_category: itemCategory,
      price: parseLocaleNumber(line?.price),
      quantity: line?.qnt ? Math.trunc(parseLocaleNumber(line.qnt)) || 1 : 1,
      selected_day: line?.exercise_startTime || undefined,
    };
  });

  return {
    transaction_id: transactionId,
    value: total,
    currency: "PLN",
    coupon: discountCode,
    discount_coupon: discountValue,
    payment_type: payName,
    items,
  };
};

/**
 * Konwertuje koszyk na format ViewCartEvent dla GTM
 * @param basket - Elementy koszyka
 * @param total - Całkowita wartość koszyka (fallback)
 * @returns Dane w formacie GTM dla eventu view_cart
 */
export const convertBasketToViewCartFormat = (basket: any[], total: number): ViewCartEvent => {
  const items = basket.map((item) => convertBasketItemToGtmItem(item));

  // Obliczaj value bezpośrednio z koszyka
  const calculatedValue = basket.reduce((acc, item) => acc + (item.price || 0) * (item.qnt || 0), 0);

  return {
    currency: "PLN",
    value: calculatedValue > 0 ? calculatedValue : total,
    items,
  };
};

/**
 * Pomocnicza funkcja do konwersji BasketItem na format GTM
 * @param basketItem - Element koszyka
 * @returns Element w formacie GTM
 */
export const convertBasketItemToGtmItem = (basketItem: any) => {
  const itemCategory =
    (basketItem.ticketAccess && basketItem.ticketAccess[0]?.exerciseName) || getPolishItemCategory(basketItem);

  let selectedDay: string | undefined;

  if (basketItem.ticketAccess && basketItem.ticketAccess[0]?.startDate) {
    try {
      const date = new Date(basketItem.ticketAccess[0].startDate);
      selectedDay = format(date, "yyyy-MM-dd");
    } catch (error) {
      console.warn("Error formatting ticketAccess startDate:", error);
    }
  } else if (basketItem.validFrom) {
    try {
      const date = new Date(basketItem.validFrom);
      selectedDay = format(date, "yyyy-MM-dd");
    } catch (error) {
      console.warn("Error formatting validFrom date:", error);
    }
  }

  const gtmItem: any = {
    item_id: basketItem.assortIds?.replace(/^0,\+/, "") || "",
    price: basketItem.price || 0,
    quantity: basketItem.qnt || 1,
    item_category: itemCategory,
  };

  // Tylko jeśli mamy nazwę, to ją dodajemy
  if (basketItem.assortName) {
    gtmItem.item_name = basketItem.assortName;
  }

  // Tylko jeśli mamy selected_day, to go dodajemy
  if (selectedDay) {
    gtmItem.selected_day = selectedDay;
  }

  return gtmItem;
};

/**
 * Pomocnicza funkcja do tłumaczenia kategorii na polskie nazwy
 * @param item - Element koszyka
 * @returns Polska nazwa kategorii lub undefined
 */
const getPolishItemCategory = (item: any): string | undefined => {
  if (item.ticket === true) {
    return "bilety";
  } else if (item.pass === true) {
    return "karnety";
  } else if (item.service === true) {
    return "usługi";
  } else if (item.accountIncome === true) {
    return "doładowanie konta";
  }
  return undefined;
};
